import type { GetTokenSilentlyOptions, IdToken } from "@auth0/auth0-react";

const SKEW_SECONDS = 60;

let cached: { raw: string; exp: number } | null = null;
let pending: Promise<string | null> | null = null;
let getters: {
  getIdTokenClaims: () => Promise<IdToken | undefined>;
  getAccessTokenSilently: (options?: GetTokenSilentlyOptions) => Promise<string>;
} | null = null;

export function configureAuth0TokenGetters(nextGetters: typeof getters) {
  getters = nextGetters;
}

export function clearAuth0SupabaseToken() {
  cached = null;
  pending = null;
}

function usable(claims: IdToken | undefined): claims is IdToken & { __raw: string; exp: number } {
  return Boolean(
    claims?.__raw &&
    claims.exp &&
    claims.exp > Math.floor(Date.now() / 1000) + SKEW_SECONDS
  );
}

// auth0-react's own getIdTokenClaims() just reads whatever is cached
// in-memory/localStorage -- it does not check expiry. A tab left open (or
// reopened) after the ID token's own (short, ~hours) lifetime has elapsed
// gets a stale, already-expired token back, even though the underlying
// Auth0 session/refresh token is still perfectly valid. Any caller that
// forwards __raw to a backend for JWT verification must go through this
// instead of calling getIdTokenClaims() directly, or it'll get a spurious
// 401 and (in useAuthUser.ts) force a full logout despite the session being
// fine -- this is what caused "signed in yesterday, asked to log in again
// today" (see docs/2026-08-01-events-session-persistence-report.md).
export async function getFreshAuth0IdTokenClaims(): Promise<IdToken | undefined> {
  if (!getters) return undefined;
  let claims = await getters.getIdTokenClaims();
  if (!usable(claims)) {
    await getters.getAccessTokenSilently({ cacheMode: "off" } as GetTokenSilentlyOptions);
    claims = await getters.getIdTokenClaims();
  }
  return claims;
}

export async function getAuth0SupabaseToken(): Promise<string | null> {
  const now = Math.floor(Date.now() / 1000);
  if (cached && cached.exp > now + SKEW_SECONDS) return cached.raw;
  if (pending) return pending;
  if (!getters) return null;

  pending = (async () => {
    const claims = await getFreshAuth0IdTokenClaims();
    if (!usable(claims)) return null;
    cached = { raw: claims.__raw, exp: claims.exp };
    return claims.__raw;
  })();

  try {
    return await pending;
  } finally {
    pending = null;
  }
}
